<template>
  <div
    @mouseover.stop.prevent="handleMouseOver"
    @mouseleave.stop.prevent="handleMouseLeave"
    @click="handleClick">
    <a
      :tabindex="menuContext.isFocusable(item) ? 0 : -1"
      :aria-haspopup="hasChildren"
      ref="a"
      :href="uri"
      :title="description"
      :class="{'vue-focus': focused}"
      :id='`menuitem_${level}_${parentIndex}_${index}`'
      @keydown.right="$refs.submenu && $refs.submenu.open()"
      @keydown.enter="(event) => enterKeydown(event, $refs)"
      @focus="$emit('onItemFocus')"
      @blur="$emit('onItemBlur')">
        <i :name="label" class="fa5" :class="`fa-${icon.name} ${icon.class}`" aria-hidden='true'></i>
        <span>{{ label }}</span>
    </a>
    <div v-if="subItem" class="sub">
      <span class="sr-only">{{LANG.currentPage}}:</span>
      <span>{{ subItem }}</span>
    </div>
    <Menu
      ref="submenu"
      v-if="hasChildren"
      @collapse="$emit('onItemFocus')"
      :items="children"
      :level="level+1"
      :parentIndex="index"
      :aria-labeledby='`menuitem_${level}_${parentIndex}_${index}`'
      :expanded="active"/>
  </div>
</template>

<script>
import { defineComponent, defineAsyncComponent } from 'vue';
import { mapActions, mapState } from 'vuex';

// dynamic import to allow recursive components:
const asyncMenu = defineAsyncComponent(() => import('./Menu.vue'));

function _normalizeSettings(settings = {}) {
  const handlerType = {
    mouseover: 'mouse',
    mouseout: 'mouse',
    click: 'click',
  }[settings.type];
  return {
    type: handlerType || 'mouse',
    delay: settings.delay || 0,
  };
}

export default defineComponent({
  components: {
    Menu: asyncMenu,
  },
  inject: ['$menuContext'],
  watch: {
    focused(v) {
      if (v) {
        this.$nextTick(() => this.$refs.a.focus());
      }
    },
    active(newValue) {
      // FIXME: move 'active' state logic to the central store/handler
      // Note: currently working for a single menu level
      if (newValue) {
        const { items } = this.left_menu;

        if (Array.isArray(items)) {
          items.forEach((item, idx) => {
            if (idx !== this.index) {
              items[idx].active = false;
            }
          });
        }
      }
    },
  },
  data() {
    return {
      menuTimeout: null,
    };
  },
  props: {
    item: Object,
    focused: Boolean,
    level: Number,
    index: Number,
    parentIndex: Number,
  },
  created() {
    // TODO: refactor as a mutation (do not mutate state in the Component!)
    this.item.active = false;
  },
  computed: {
    ...mapState(['LANG', 'left_menu']),
    menuContext() {
      return typeof this.$menuContext === 'function' ? this.$menuContext() : null;
    },
    hasChildren() {
      return Boolean(this.children && this.children.length);
    },
    active() {
      return this.item.active;
    },
    label() {
      return this.item.values.label;
    },
    uri() {
      return this.item.values.uri;
    },
    description() {
      return this.item.values.description;
    },
    icon() {
      return { name: this.item.values.icon, class: this.item.values.icon_class };
    },
    children() {
      return this.item.children;
    },
    subItem() {
      return this.item.currentSubPage;
    },
    menuOpenHandler() {
      return _normalizeSettings(this.left_menu.settings?.open);
    },
    menuCloseHandler() {
      return _normalizeSettings(this.left_menu.settings?.close);
    },
  },
  methods: {
    ...mapActions('left_menu', ['navigateTo']),
    toggleMenu(open, delay = 0) {
      // FIXME: currently, only one-level is supported
      if (this.level !== 0) return;

      clearTimeout(this.menuTimeout);
      this.menuTimeout = setTimeout(
        () => {
          this.item.active = open;
        },
        delay,
      );
    },
    handleClick() {
      const isOpen = this.item.active;
      const handler = isOpen ? this.menuCloseHandler : this.menuOpenHandler;
      if (handler.type !== 'click') return;
      this.toggleMenu(!isOpen);
    },
    handleMouseOver() {
      const handler = this.menuOpenHandler;
      if (this.menuOpenHandler.type !== 'mouse') return;
      this.toggleMenu(true, handler.delay);
    },
    handleMouseLeave() {
      const handler = this.menuCloseHandler;
      if (handler.type !== 'mouse') return;
      this.toggleMenu(false, handler.delay);
    },
    enterKeydown(event, $refs) {
      if ($refs.submenu) {
        $refs.submenu.open();
        event.preventDefault();
      }
    },
  },
});

</script>

<style scoped  lang='scss'>
</style>
